import { useRef, useState, useCallback, useEffect } from 'react';
import { spacing, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * HSV to Hex conversion
 */
const hsvToHex = (h, s, v) => {
  const f = (n) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const r = Math.round(f(5) * 255);
  const g = Math.round(f(3) * 255);
  const b = Math.round(f(1) * 255);
  return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Hex to HSV conversion
 */
const hexToHsv = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 0, 0];

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return [h * 360, s, v];
};

/**
 * DualColorPicker Component
 *
 * A compact color picker with toggle between background and foreground colors.
 */
const DEFAULT_BACKGROUND = '#F7F7F7';
const DEFAULT_FOREGROUND = '#171717';

const DualColorPicker = ({
  label,
  backgroundColor = DEFAULT_BACKGROUND,
  foregroundColor = DEFAULT_FOREGROUND,
  onBackgroundColorChange,
  onForegroundColorChange,
  disabled = false
}) => {
  const { colors } = useTheme();
  const [activeColor, setActiveColor] = useState('background'); // 'background' or 'foreground'
  const currentValue = activeColor === 'background' ? backgroundColor : foregroundColor;
  const currentOnChange = activeColor === 'background' ? onBackgroundColorChange : onForegroundColorChange;

  const [hsv, setHsv] = useState(() => hexToHsv(currentValue));
  const [isDraggingArea, setIsDraggingArea] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);
  const [hexInput, setHexInput] = useState(currentValue.toUpperCase());
  const [isEditingHex, setIsEditingHex] = useState(false);
  const areaRef = useRef(null);
  const hueRef = useRef(null);

  // Sync when switching between colors or external value changes
  useEffect(() => {
    setHsv(hexToHsv(currentValue));
    if (!isEditingHex) {
      setHexInput(currentValue.toUpperCase());
    }
  }, [currentValue, activeColor, isEditingHex]);

  const updateColor = useCallback((newHsv) => {
    setHsv(newHsv);
    const hex = hsvToHex(newHsv[0], newHsv[1], newHsv[2]);
    currentOnChange?.(hex);
  }, [currentOnChange]);

  // Area (saturation/value) handlers
  const handleAreaInteraction = useCallback((e) => {
    if (!areaRef.current || disabled) return;

    const rect = areaRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

    updateColor([hsv[0], x, 1 - y]);
  }, [hsv, updateColor, disabled]);

  const handleAreaMouseDown = (e) => {
    if (disabled) return;
    setIsDraggingArea(true);
    handleAreaInteraction(e);
  };

  // Hue slider handlers
  const handleHueInteraction = useCallback((e) => {
    if (!hueRef.current || disabled) return;

    const rect = hueRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    updateColor([x * 360, hsv[1], hsv[2]]);
  }, [hsv, updateColor, disabled]);

  const handleHueMouseDown = (e) => {
    if (disabled) return;
    setIsDraggingHue(true);
    handleHueInteraction(e);
  };

  // Global mouse/touch move and up handlers
  useEffect(() => {
    const handleMove = (e) => {
      if (isDraggingArea) {
        handleAreaInteraction(e);
      } else if (isDraggingHue) {
        handleHueInteraction(e);
      }
    };

    const handleUp = () => {
      setIsDraggingArea(false);
      setIsDraggingHue(false);
    };

    if (isDraggingArea || isDraggingHue) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
  }, [isDraggingArea, isDraggingHue, handleAreaInteraction, handleHueInteraction]);

  const currentHex = hsvToHex(hsv[0], hsv[1], hsv[2]);
  const hueColor = hsvToHex(hsv[0], 1, 1);

  const isDefault = backgroundColor.toUpperCase() === DEFAULT_BACKGROUND.toUpperCase() &&
                    foregroundColor.toUpperCase() === DEFAULT_FOREGROUND.toUpperCase();

  const handleReset = () => {
    onBackgroundColorChange?.(DEFAULT_BACKGROUND);
    onForegroundColorChange?.(DEFAULT_FOREGROUND);
    setHsv(hexToHsv(activeColor === 'background' ? DEFAULT_BACKGROUND : DEFAULT_FOREGROUND));
  };

  return (
    <div style={{ width: '100%', opacity: disabled ? 0.5 : 1 }}>
      {label && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <label style={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: colors.foreground
          }}>
            {label}
          </label>
          {!isDefault && (
            <button
              onClick={handleReset}
              disabled={disabled}
              style={{
                fontSize: '0.75rem',
                color: colors.foregroundSecondary,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
                transition: 'color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (!disabled) e.target.style.color = colors.foreground;
              }}
              onMouseLeave={(e) => {
                e.target.style.color = colors.foregroundSecondary;
              }}
            >
              Reset
            </button>
          )}
        </div>
      )}

      {/* Toggle between Background and Characters */}
      <div style={{
        display: 'flex',
        gap: spacing.xs,
        marginBottom: spacing.md,
        background: colors.backgroundOffset,
        borderRadius: borderRadius.sm,
        padding: '3px'
      }}>
        {[
          { id: 'background', label: 'Background', color: backgroundColor },
          { id: 'foreground', label: 'Characters', color: foregroundColor }
        ].map((option) => (
          <button
            key={option.id}
            onClick={() => setActiveColor(option.id)}
            disabled={disabled}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.xs,
              padding: `${spacing.xs} ${spacing.sm}`,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: activeColor === option.id ? colors.foreground : colors.foregroundSecondary,
              background: activeColor === option.id ? colors.backgroundPanel : 'transparent',
              border: 'none',
              borderRadius: borderRadius.xs || '6px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s'
            }}
          >
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '3px',
              background: option.color,
              border: `1px solid ${colors.border}`,
              flexShrink: 0
            }} />
            {option.label}
          </button>
        ))}
      </div>

      {/* Color Area (Saturation/Value) - Full width */}
      <div
        ref={areaRef}
        onMouseDown={handleAreaMouseDown}
        onTouchStart={handleAreaMouseDown}
        style={{
          position: 'relative',
          width: '100%',
          height: '120px',
          borderRadius: borderRadius.md,
          cursor: disabled ? 'not-allowed' : 'crosshair',
          overflow: 'hidden',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {/* Base hue color */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: hueColor,
        }} />

        {/* White gradient (left to right) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to right, white, transparent)',
        }} />

        {/* Black gradient (bottom to top) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, black, transparent)',
        }} />

        {/* Picker indicator */}
        <div style={{
          position: 'absolute',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.3), inset 0 0 0 1px rgba(0,0,0,0.3)',
          transform: 'translate(-50%, -50%)',
          left: `${hsv[1] * 100}%`,
          top: `${(1 - hsv[2]) * 100}%`,
          pointerEvents: 'none',
        }} />
      </div>

      {/* Hue Slider */}
      <div
        ref={hueRef}
        onMouseDown={handleHueMouseDown}
        onTouchStart={handleHueMouseDown}
        style={{
          position: 'relative',
          height: '14px',
          borderRadius: borderRadius.full,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
          touchAction: 'none',
          userSelect: 'none',
          marginTop: spacing.sm,
        }}
      >
        {/* Hue indicator */}
        <div style={{
          position: 'absolute',
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
          transform: 'translateX(-50%)',
          left: `${(hsv[0] / 360) * 100}%`,
          top: 0,
          pointerEvents: 'none',
          background: hueColor,
        }} />
      </div>

      {/* Hex Input */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        marginTop: spacing.md
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: borderRadius.sm,
          background: currentHex,
          border: `1px solid ${colors.border}`,
          flexShrink: 0,
        }} />

        <input
          type="text"
          value={hexInput}
          onChange={(e) => {
            const newHex = e.target.value;
            setHexInput(newHex);
            if (/^#[0-9A-Fa-f]{6}$/i.test(newHex)) {
              const newHsv = hexToHsv(newHex);
              setHsv(newHsv);
              currentOnChange?.(newHex.toUpperCase());
            }
          }}
          disabled={disabled}
          style={{
            flex: 1,
            padding: `${spacing.xs} ${spacing.sm}`,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: colors.foreground,
            background: colors.backgroundPanel,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.sm,
            outline: 'none',
            minWidth: 0,
          }}
          onFocus={(e) => {
            if (!disabled) {
              e.target.style.borderColor = colors.foreground;
              setIsEditingHex(true);
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = colors.border;
            setIsEditingHex(false);
            // Reset to valid value if input is invalid
            if (!/^#[0-9A-Fa-f]{6}$/i.test(hexInput)) {
              setHexInput(currentValue.toUpperCase());
            }
          }}
        />
      </div>
    </div>
  );
};

export default DualColorPicker;
